import { FunctionComponent, useEffect, useState } from "react";
import { RoleNames, useHasAccess, WotcIdentity } from "../../hooks/useHasAccess";
import {
    Avatar,
    Box,
    Button,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    HStack,
    Heading,
    Select,
    Skeleton,
    Tag,
    Text,
    Tooltip,
    VStack,
    useClipboard,
} from "@chakra-ui/react";
import { IconCheck, IconClipboardCopy, IconCode, IconSettings2, IconUser } from "@tabler/icons-react";
import { useApiContext } from "@am-tax/fe-core";
import { RequireRole } from "../../components/RequireRole";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { format } from "date-fns";
import { useAccount, useAccountUpdate, ApplicationRole } from "../../api";
import { SetPageTitle } from "../../components/SetPageTitle";

type Section = "profile" | "preferences" | "developer";

const avatarColors = ["blue.500", "teal.500", "purple.500", "orange.500", "pink.500", "cyan.500"];

function getAvatarColor(name: string): string {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return avatarColors[Math.abs(hash) % avatarColors.length];
}

export const ProfilePage: FunctionComponent = () => {
    const { identity } = useHasAccess(),
        appInsights = useAppInsightsContext();

    useEffect(() => {
        appInsights.trackPageView({ uri: "/profile" });
    }, [appInsights]);

    return (
        <Box p={8} maxW="1000px" mx="auto">
            <SetPageTitle title={"Your Profile"} />
            {identity ? <ProfileLayout identity={identity} /> : <Text fontSize="lg">No identity found.</Text>}
        </Box>
    );
};

const now = new Date();

const ProfileLayout: FunctionComponent<{ identity: WotcIdentity }> = ({ identity }) => {
    const [activeSection, setActiveSection] = useState<Section>("profile");
    const { hasRole } = useHasAccess();
    const isDeveloper = hasRole(ApplicationRole.developer);

    const displayName = `${identity.givenname} ${identity.surname}`;
    const avatarColor = getAvatarColor(displayName);
    const initials = `${identity.givenname?.[0] ?? ""}${identity.surname?.[0] ?? ""}`.toUpperCase();

    const navItems: { id: Section; label: string; icon: React.ReactNode; hidden?: boolean }[] = [
        { id: "profile", label: "Profile", icon: <IconUser size={16} /> },
        { id: "preferences", label: "Preferences", icon: <IconSettings2 size={16} /> },
        { id: "developer", label: "Developer settings", icon: <IconCode size={16} />, hidden: !isDeveloper },
    ];

    return (
        <Flex gap={10} align="flex-start">
            {/* Left sidebar */}
            <Box w="240px" flexShrink={0} position="sticky" top={8}>
                <VStack spacing={3} align="center" mb={6}>
                    <Avatar size="xl" name={displayName} bg={avatarColor} color="white" getInitials={() => initials} />
                    <VStack spacing={0} align="center">
                        <Text fontWeight="semibold" fontSize="md">
                            {displayName}
                        </Text>
                        <Text fontSize="sm" color="gray.500" wordBreak="break-all" textAlign="center">
                            {identity.mail}
                        </Text>
                    </VStack>
                </VStack>

                <Divider mb={3} />

                <VStack spacing={1} align="stretch">
                    {navItems
                        .filter(item => !item.hidden)
                        .map(item => {
                            const isActive = activeSection === item.id;
                            return (
                                <Flex
                                    key={item.id}
                                    as="button"
                                    onClick={() => setActiveSection(item.id)}
                                    align="center"
                                    gap={2.5}
                                    px={3}
                                    py={2}
                                    borderRadius="md"
                                    borderLeft={isActive ? "3px solid" : "3px solid transparent"}
                                    borderColor={isActive ? "blue.500" : "transparent"}
                                    bg={isActive ? "blue.50" : "transparent"}
                                    color={isActive ? "blue.700" : "gray.700"}
                                    fontWeight={isActive ? "semibold" : "normal"}
                                    fontSize="sm"
                                    textAlign="left"
                                    transition="all 0.15s"
                                    _hover={{ bg: isActive ? "blue.50" : "gray.100" }}
                                    w="full"
                                >
                                    <Box flexShrink={0} color={isActive ? "blue.500" : "gray.500"}>
                                        {item.icon}
                                    </Box>
                                    {item.label}
                                </Flex>
                            );
                        })}
                </VStack>
            </Box>

            {/* Right content */}
            <Box flex={1} minW={0}>
                {activeSection === "profile" && <ProfileSection identity={identity} />}
                {activeSection === "preferences" && <PreferencesSection />}
                {activeSection === "developer" && (
                    <RequireRole role={ApplicationRole.developer}>
                        <DeveloperSection />
                    </RequireRole>
                )}
            </Box>
        </Flex>
    );
};

const SectionHeader: FunctionComponent<{ title: string; description?: string }> = ({ title, description }) => (
    <Box mb={6}>
        <Heading size="md" mb={1}>
            {title}
        </Heading>
        {description && (
            <Text fontSize="sm" color="gray.500">
                {description}
            </Text>
        )}
        <Divider mt={4} />
    </Box>
);

const FieldRow: FunctionComponent<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Box>
        <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color="gray.500" mb={1}>
            {label}
        </Text>
        {children}
    </Box>
);

const ProfileSection: FunctionComponent<{ identity: WotcIdentity }> = ({ identity }) => {
    const roles = (identity.roles ?? []).map(r => RoleNames[r]);
    const idClipboard = useClipboard(identity.oid);

    return (
        <Box>
            <SectionHeader title="Profile" description="Your account information from the identity provider." />
            <VStack spacing={6} align="stretch">
                <FieldRow label="Name">
                    <Text fontSize="sm">
                        {identity.givenname} {identity.surname}
                    </Text>
                </FieldRow>
                <FieldRow label="Email">
                    <Text fontSize="sm">{identity.mail}</Text>
                </FieldRow>
                <FieldRow label="ID">
                    <Tooltip label={idClipboard.hasCopied ? "Copied!" : "Click to copy"} placement="right">
                        <Text fontSize="sm" fontFamily="mono" color="gray.600" cursor="copy" onClick={idClipboard.onCopy} display="inline-block">
                            {identity.oid}
                        </Text>
                    </Tooltip>
                </FieldRow>
                <FieldRow label="Roles">
                    <HStack mt={1} flexWrap="wrap">
                        {roles.map(role => (
                            <Tag key={role} size="md" colorScheme="blue" borderRadius="full">
                                {role}
                            </Tag>
                        ))}
                    </HStack>
                </FieldRow>
            </VStack>
        </Box>
    );
};

const PreferencesSection: FunctionComponent = () => {
    const { data: account, isLoading: accountLoading } = useAccount();
    const { mutate: updateAccount, isLoading: accountSaving } = useAccountUpdate();
    const isLoading = accountLoading || accountSaving;

    const dateFormatOptions = dateFormats.map(f => (
        <option key={f} value={f}>
            {format(now, f)}
        </option>
    ));
    const timeFormatOptions = timeFormats.map(f => (
        <option key={f} value={f}>
            {format(now, f)}
        </option>
    ));

    return (
        <Box>
            <SectionHeader title="Preferences" description="Customize how dates and times are displayed across the application." />
            <Skeleton isLoaded={!accountLoading}>
                <VStack spacing={6} align="stretch" maxW="400px">
                    <FormControl isDisabled={isLoading}>
                        <FormLabel fontSize="sm" fontWeight="medium">
                            Date format
                        </FormLabel>
                        <Select
                            size="sm"
                            value={account?.dateFormat}
                            onChange={ev => {
                                const { selectedOptions } = ev.target;
                                updateAccount([{ op: "replace", path: "/dateFormat", value: selectedOptions[0].value }]);
                            }}
                        >
                            {dateFormatOptions}
                        </Select>
                    </FormControl>
                    <FormControl isDisabled={isLoading}>
                        <FormLabel fontSize="sm" fontWeight="medium">
                            Time format
                        </FormLabel>
                        <Select
                            size="sm"
                            value={account?.timeFormat}
                            onChange={ev => {
                                const { selectedOptions } = ev.target;
                                updateAccount([{ op: "replace", path: "/timeFormat", value: selectedOptions[0].value }]);
                            }}
                        >
                            {timeFormatOptions}
                        </Select>
                    </FormControl>
                </VStack>
            </Skeleton>
        </Box>
    );
};

const DeveloperSection: FunctionComponent = () => {
    const { accessToken, idToken } = useApiContext(),
        appInsights = useAppInsightsContext();

    const { onCopy: onApiTokenCopy, hasCopied: apiTokenHasCopied } = useClipboard(accessToken ?? ""),
        onApiTokenClick = () => {
            onApiTokenCopy();
            appInsights.trackEvent({ name: "profile.apiTokenCopy" });
        };

    const { onCopy: onAppTokenCopy, hasCopied: appTokenHasCopied } = useClipboard(idToken ?? ""),
        onAppTokenClick = () => {
            onAppTokenCopy();
            appInsights.trackEvent({ name: "profile.appTokenCopy" });
        };

    return (
        <Box>
            <SectionHeader
                title="Developer settings"
                description="Copy bearer tokens for use with API tools like Postman or curl. Tokens expire when your session ends."
            />
            <VStack spacing={4} align="flex-start">
                <Box p={4} borderWidth="1px" borderRadius="md" borderColor="gray.200" w="full">
                    <Text fontSize="sm" fontWeight="medium" mb={1}>
                        API Token (access_token)
                    </Text>
                    <Text fontSize="xs" color="gray.500" mb={3}>
                        Bearer token for authenticating against the WOTC API endpoints.
                    </Text>
                    <Button
                        size="sm"
                        colorScheme={apiTokenHasCopied ? "green" : "gray"}
                        variant="outline"
                        leftIcon={apiTokenHasCopied ? <IconCheck size={16} /> : <IconClipboardCopy size={16} />}
                        onClick={onApiTokenClick}
                    >
                        {apiTokenHasCopied ? "Copied!" : "Copy API Token"}
                    </Button>
                </Box>

                <Box p={4} borderWidth="1px" borderRadius="md" borderColor="gray.200" w="full">
                    <Text fontSize="sm" fontWeight="medium" mb={1}>
                        App Token (id_token)
                    </Text>
                    <Text fontSize="xs" color="gray.500" mb={3}>
                        Identity token issued by the identity provider for this application.
                    </Text>
                    <Button
                        size="sm"
                        colorScheme={appTokenHasCopied ? "green" : "gray"}
                        variant="outline"
                        leftIcon={appTokenHasCopied ? <IconCheck size={16} /> : <IconClipboardCopy size={16} />}
                        onClick={onAppTokenClick}
                    >
                        {appTokenHasCopied ? "Copied!" : "Copy App Token"}
                    </Button>
                </Box>
            </VStack>
        </Box>
    );
};

const dateFormats = ["MM/dd/yyyy", "MM-dd-yyyy", "M/d/yyyy", "M-d-yyyy", "yyyy/MM/dd", "yyyy-MM-dd", "MMM d, yyyy"];

const timeFormats = ["hh:mm a", "H:mm"];
